import { AcceptedIntentSchema, type AcceptedIntent, type RunState } from '@dissidium/contracts';
import { recordDeath, reduceIntent } from '@dissidium/sim';
import { SaveCorruptError } from '../errors.ts';

/**
 * The two operations the kernel accepts as irreversible run progress: a ticked, sequenced
 * PC intention, and a death commit. Nothing else may enter the rolling journal.
 */
export type DurableOperation =
  | Readonly<{ kind: 'intent'; intent: AcceptedIntent }>
  | Readonly<{ kind: 'death'; personId: string; eventId: string }>;

/** One write-ahead record: what was accepted, and the canonical hashes either side of it. */
export type JournalRecord<Operation = DurableOperation> = Readonly<{
  sequence: number;
  digestBefore: string;
  digestAfter: string;
  operation: Operation;
}>;

const DIGEST = /^[0-9a-f]{16}$/;
const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value) && Object.getPrototypeOf(value) === Object.prototype;

export function parseOperation(input: unknown): DurableOperation {
  if (!isRecord(input)) throw new SaveCorruptError('A journal record has no durable operation');
  if (input.kind === 'intent') return { kind: 'intent', intent: AcceptedIntentSchema.parse(input.intent) };
  if (input.kind === 'death') {
    if (typeof input.personId !== 'string' || !input.personId || typeof input.eventId !== 'string' || !input.eventId) {
      throw new SaveCorruptError('A death record needs one person and one event');
    }
    return { kind: 'death', personId: input.personId, eventId: input.eventId };
  }
  throw new SaveCorruptError('The journal contains an unknown durable operation');
}

export function parseJournalRecord<O = DurableOperation>(input: unknown, parse: (input: unknown) => O = parseOperation as (input: unknown) => O): JournalRecord<O> {
  if (!isRecord(input) || !Number.isSafeInteger(input.sequence) || Number(input.sequence) < 0) {
    throw new SaveCorruptError('Journal records need a non-negative integer sequence');
  }
  if (typeof input.digestBefore !== 'string' || !DIGEST.test(input.digestBefore) ||
      typeof input.digestAfter !== 'string' || !DIGEST.test(input.digestAfter)) {
    throw new SaveCorruptError('Journal records need canonical digests either side of the operation');
  }
  return { sequence: Number(input.sequence), digestBefore: input.digestBefore, digestAfter: input.digestAfter, operation: parse(input.operation) };
}

/** Contiguous from `startSequence`, and each record must chain onto the one before it. */
export function validateJournal<O = DurableOperation>(input: unknown, startSequence = 0, parse: (input: unknown) => O = parseOperation as (input: unknown) => O): JournalRecord<O>[] {
  if (!Array.isArray(input)) throw new SaveCorruptError('A rolling journal is an ordered list of records');
  const records = input.map(record => parseJournalRecord(record, parse));
  records.forEach((record, index) => {
    if (record.sequence !== startSequence + index) {
      throw new SaveCorruptError(`The rolling journal is not contiguous: expected sequence ${startSequence + index}, found ${record.sequence}`);
    }
    if (index > 0 && records[index - 1]!.digestAfter !== record.digestBefore) {
      throw new SaveCorruptError(`The rolling journal chain breaks at sequence ${record.sequence}`);
    }
  });
  return records;
}

/** The one place an operation becomes state; recovery and live play share it. */
export function applyOperation(state: RunState, operation: DurableOperation): RunState {
  return operation.kind === 'intent'
    ? reduceIntent(state, operation.intent)
    : recordDeath(state, operation.personId, operation.eventId);
}
